import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Icon,
  useTheme,
  alpha,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import { OrderResponse } from "../../types/pos.types";
import { formatCurrency } from "../../utils/formatters";
import moment from "moment";
import { SupervisorPinModal } from "../SuperuserPinModal/SuperuserPinModal";
import { useUserRole } from "../../hooks/useUserRole";
import { posService } from "../../../../services/pos.service";
import { toast } from "react-toastify";
import { AuditLogData, publicService } from "../../../../services/public.service";
import { useAuth } from "../../../auth/hooks/useAuth";
import { hasPermission } from "../../../auth/utils/permission.utils";

interface OrdersListProps {
  orders: OrderResponse[];
  loading: boolean;
  onVoidOrder: (orderId: string, reason: string, user?: any) => void;
  onContinueOrder: (order: OrderResponse) => void;
  onUpdateOrder: (order: OrderResponse) => void;
  onResumeOrder: (order: OrderResponse) => void;
  onPayOrder: (order: OrderResponse) => void;
  is_pos_supervisor: boolean;
  refresh: number;
  setRefresh: React.Dispatch<React.SetStateAction<number>>;
}

export const OrdersList: React.FC<OrdersListProps> = ({
  orders,
  loading,
  onVoidOrder,
  onContinueOrder,
  onPayOrder,
  refresh,
  setRefresh,
  onUpdateOrder,
  onResumeOrder,
  is_pos_supervisor,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] =
    React.useState<OrderResponse | null>(null);
  const [selectedOrderForVoid, setSelectedOrderForVoid] =
    React.useState<OrderResponse | null>(null);
  const [voidReason, setVoidReason] = React.useState("");
  const [isVoiding, setIsVoiding] = React.useState(false);
  const [selectedStatus, setSelectedStatus] = React.useState<string>("all");
  const [showPinModal, setShowPinModal] = React.useState(false);
  const [orderPendingVoid, setOrderPendingVoid] = React.useState<OrderResponse | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);


  const isSuperAdminOrStoreAdmin = useUserRole();



  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    order: OrderResponse
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleVoidClick = (order: OrderResponse) => {
    setIsProcessing(true);
    setOrderPendingVoid(order);
    setShowPinModal(true); // Always open PIN modal
    setIsProcessing(false);
  };


  const handleUpdateOrderStatus = async (
    order: OrderResponse,
    newStatus: string,
    description?: string
  ) => {
    try {
      const result = await posService.updateOrderStatus({
        order_id: order.order_id,
        status: newStatus,
        action_description: description || `Order status updated to ${newStatus}`,
      });

      if (result.success) {
        toast.success(description || `Order status updated to "${newStatus}"`);

        // Update local UI with latest order data
        onUpdateOrder(result.data);

        // If status is "pending_payment", trigger payment flow
        if (newStatus === "pending_payment") {
          onPayOrder(order); // 👈 Calls navigation + localStorage logic
        }

        // Refresh list
        setRefresh((prev) => prev + 1);
      } else {
        toast.error(result.message || `Failed to update order to "${newStatus}"`);
      }
    } catch (err: any) {
      toast.error(err.message || "An unexpected error occurred");
    }
  };



  const handleVoidConfirm = async () => {
    if (selectedOrderForVoid && voidReason.trim()) {
      setIsVoiding(true);
      setIsProcessing(true);
      try {
        const result = await onVoidOrder(selectedOrderForVoid.order_id, voidReason.trim());

      } catch (error) {
        console.error("Error voiding order:", error);

      } finally {
        setIsVoiding(false);
        setSelectedOrderForVoid(null);
        setVoidReason("");
        setIsProcessing(false);
      }
    }
  };



  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return {
          color: "#059669",
          backgroundColor: "#ECFDF5",
          borderColor: "#A7F3D0"
        };
      case "completed": // ✅ Add style for completed
        return {
          color: "#059669",
          backgroundColor: "#ECFDF5",
          borderColor: "#A7F3D0"
        };
      case "pending_payment":
        return {
          color: "#D97706",
          backgroundColor: "#FFFBEB",
          borderColor: "#FCD34D"
        };
      case "draft":
        return {
          color: "#2563EB",
          backgroundColor: "#EFF6FF",
          borderColor: "#93C5FD"
        };
      case "voided":
        return {
          color: "#DC2626",
          backgroundColor: "#FEF2F2",
          borderColor: "#FECACA"
        };
      case "sent":
        return {
          color: "#2563EB",
          backgroundColor: "#EFF6FF",
          borderColor: "#93C5FD"
        };
      case "served":
        return {
          color: "#7C3AED",
          backgroundColor: "#F5F3FF",
          borderColor: "#C4B5FD"
        };
      default:
        return {
          color: "#4B5563",
          backgroundColor: "#F3F4F6",
          borderColor: "#D1D5DB"
        };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "check_circle";
      case "pending_payment":
        return "pending";
      case "draft":
        return "pause_circle";
      case "voided":
        return "cancel";
      case "sent":
        return "pause_circle";
      case "served":
        return "check_circle_outline";
      default:
        return "help";
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case "cash":
        return "payments";
      case "card":
      case "mastercard":
        return "credit_card";
      case "mobile":
      case "benefitpay":
        return "phone_android";
      default:
        return "payment";
    }
  };

  const formatPaymentMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "mastercard":
        return "CARD";
      case "benefitpay":
        return "MOBILE";
      default:
        return method.toUpperCase();
    }
  };

  const getVoidButtonTooltip = (order: OrderResponse) => {
    if (!isSuperAdminOrStoreAdmin && !is_pos_supervisor) {
      return "Only POS supervisors can void orders";
    }

    if (["served", "pending_payment", "paid"].includes(order.status)) {
      return "Void not allowed for this status";
    }

    if (order.status === "voided") {
      return "This order has already been voided";
    }

    return "Void Order";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: 2,
          p: 3,
        }}
      >
        <Icon sx={{ fontSize: 48, color: "text.secondary" }}>receipt_long</Icon>
        <Typography variant="h6" color="text.secondary">
          No Orders Found
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Create a new order to get started
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedStatus}
          onChange={(e, newValue) => setSelectedStatus(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="order status tabs"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#1E8078",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#1E8078",
            },
          }}
        >
          <Tab label="All" value="all" />
          <Tab label="Draft" value="draft" />
          <Tab label="Sent" value="sent" />
          <Tab label="Served" value="served" />
          <Tab label="Paid" value="paid" />
          {/* Optional additional statuses */}
          <Tab label="On Hold" value="on_hold" />
          <Tab label="Pending Payment" value="pending_payment" />
        </Tabs>
      </Box>
      <TableContainer sx={{ height: "100%" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Subtotal</TableCell>
              <TableCell>VAT</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.filter(order => {
              if (selectedStatus === "all") return true;
              return order.status.toLowerCase() === selectedStatus;
            }).map((order, index) => (
              <TableRow
                key={order.order_id}
                sx={{
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {order.order_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {moment(order.order_date).format("DD/MM/YY")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {moment(order.order_date).format("HH:mm")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.items.length} items
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.items
                        .map((item: { name: string }) => item.name)
                        .join(", ")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography>
                      {order.payment && "method" in order.payment
                        ? formatPaymentMethod(order.payment.method)
                        : "Draft"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatCurrency(order.raw_price)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(order.tax)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(order.total_price)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={
                      <Icon sx={{
                        fontSize: 18,
                        color: getStatusColor(order.status).color,
                        marginLeft: "2px"
                      }}>
                        {getStatusIcon(order.status)}
                      </Icon>
                    }
                    label={order.status.replace(/_/g, " ")}
                    size="small"
                    sx={{
                      minWidth: 100,
                      height: "32px",
                      color: getStatusColor(order.status).color,
                      backgroundColor: getStatusColor(order.status).backgroundColor,
                      border: "1px solid",
                      borderColor: getStatusColor(order.status).borderColor,
                      borderRadius: "16px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: getStatusColor(order.status).backgroundColor,
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      },
                      "& .MuiChip-label": {
                        px: 1.5,
                        paddingLeft: "6px",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        letterSpacing: "0.01em"
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    {/* Draft: Resume */}
                    {order.status === "draft" && (
                      <Tooltip title="Resume Order">
                        <Button
                          startIcon={<Icon>start</Icon>}
                          variant="outlined"
                          size="small"
                          onClick={() => onResumeOrder(order)}
                          sx={{
                            minWidth: "auto",
                            px: 1.5,
                            py: 0.5,
                            color: theme.palette.success.main,
                            borderColor: theme.palette.success.main,
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.success.main, 0.1),
                              borderColor: theme.palette.success.main
                            },
                            "&.Mui-disabled": {
                              color: theme.palette.text.disabled,
                              borderColor: theme.palette.divider,
                            },
                          }}
                        >
                          Continue Order
                        </Button>
                      </Tooltip>
                    )}
                    {/* Draft: Send to Kitchen */}
                    {order.status === "draft" && order.orderType === "dine_in" && (
                                              <Button
                          startIcon={<Icon>send</Icon>}
                          variant="outlined"
                          size="small"
                          disabled={!hasPermission("pos.orders.send_to_kitchen", user?.newPermissions, user?.assignment_details?.id || "", user?.is_store_owner || false)}
                          onClick={() => handleUpdateOrderStatus(order, "sent", "Order sent to kitchen")}
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          color: theme.palette.action.active,
                          borderColor: theme.palette.action.active,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.action.active, 0.1),
                            borderColor: theme.palette.action.active
                          },
                          "&.Mui-disabled": {
                            color: theme.palette.text.disabled,
                            borderColor: theme.palette.divider,
                          },
                        }}
                      >
                        Send to Kitchen
                      </Button>
                    )}

                    {/* Sent: Mark as Served (but sets status to pending_payment) */}
                    {order.status === "sent" && (
                      <>
                        <Tooltip title={"Mark as Served"}>
                          <span>
                            <Button
                              size="small"
                              startIcon={<Icon>check_circle</Icon>}
                              variant="outlined"
                              color="success"
                              disabled={!hasPermission("pos.orders.mark_as_served", user?.newPermissions, user?.assignment_details?.id || "", user?.is_store_owner || false)}
                              onClick={() => handleUpdateOrderStatus(order, "served", "Order marked as served")}
                              sx={{
                                minWidth: "auto",
                                px: 1.5,
                                py: 0.5,
                                color: theme.palette.success.main,
                                borderColor: theme.palette.success.main,
                                "&:hover": {
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  borderColor: theme.palette.success.main
                                },
                                "&.Mui-disabled": {
                                  color: theme.palette.text.disabled,
                                  borderColor: theme.palette.divider,
                                },
                              }}
                            >
                              Mark As Served
                            </Button>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {/* Pending Payment: Show Pay Button */}
                    {order.status === "pending_payment" && (
                      <Tooltip title="Proceed to Payment">
                        <Button
                          size="small"
                          startIcon={<Icon>payment</Icon>}
                          variant="contained"
                          color="primary"
                          onClick={() => onPayOrder(order)}
                          sx={{
                            minWidth: "auto",
                            px: 1.5,
                            py: 0.5,
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.dark,
                            },
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            borderRadius: 1.5,
                          }}
                        >
                          Pay
                        </Button>
                      </Tooltip>
                    )}

                    {/* Served: Proceed to Payment */}
                    {order.status === "served" && (
                      <>
                        {/* Proceed to Payment Button */}
                        <Tooltip title="Proceed to Payment">
                          <Button
                            size="small"
                            startIcon={<Icon>payment</Icon>}
                            variant="contained"
                            color="primary"
                            onClick={() => handleUpdateOrderStatus(order, "pending_payment", "Payment initiated")}
                            sx={{
                              minWidth: "auto",
                              px: 1.5,
                              py: 0.5,
                              backgroundColor: theme.palette.primary.main,
                              color: "#fff",
                              "&:hover": {
                                backgroundColor: theme.palette.primary.dark,
                              },
                              textTransform: "none",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          >
                            Proceed to Payment
                          </Button>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title={getVoidButtonTooltip(order)}>
                      <span>
                        <Button
                          size="small"
                          onClick={() => handleVoidClick(order)}
                          disabled={!hasPermission("pos.orders.void_order", user?.newPermissions, user?.assignment_details?.id || "", user?.is_store_owner || false) || !["draft", "sent", "on_hold"].includes(order.status)}
                          startIcon={<Icon>block</Icon>}
                          variant="outlined"
                          color="error"
                          sx={{
                            minWidth: "auto",
                            px: 1.5,
                            py: 0.5,
                            color: theme.palette.error.main,
                            borderColor: theme.palette.error.main,
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              borderColor: theme.palette.error.main,
                            },
                            "&.Mui-disabled": {
                              color: theme.palette.text.disabled,
                              borderColor: theme.palette.divider,
                            },
                          }}
                        >
                          Void
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Void Order Confirmation Dialog */}
      <Dialog
        open={!!selectedOrderForVoid}
        onClose={() => setSelectedOrderForVoid(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.error.main }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon>warning</Icon>
            Void Order
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to void this order? This action cannot be
            undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Order ID: {selectedOrderForVoid?.order_id}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Amount: BHD {selectedOrderForVoid?.total_price.toFixed(3)}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Void"
            fullWidth
            multiline
            rows={3}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Please provide a reason for voiding this order..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setSelectedOrderForVoid(null)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleVoidConfirm}
            variant="contained"
            color="error"
            disabled={!voidReason.trim() || isVoiding}
            startIcon={
              isVoiding ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>block</Icon>
              )
            }
          >
            {isVoiding ? "Voiding..." : "Void Order"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* supervisor pin modal */}
      <SupervisorPinModal
        open={showPinModal}
        action="void_order"
        reasonLabel="Reason for Void"
        onClose={() => {
          setShowPinModal(false);
          setOrderPendingVoid(null);
        }}
        onSuccess={(reason) => {
          if (orderPendingVoid && reason.trim()) {
            setSelectedOrderForVoid(orderPendingVoid); // ✅ Now it's safe to set
            setVoidReason(reason);                    // Pre-fill reason
            setOrderPendingVoid(null);
          }
          setShowPinModal(false);
        }}
      />
      {isProcessing && (
        <Box sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(255,255,255,0.7)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
};
